export const billingConfiguration = {
  header: {
    title: "Configuración de facturación",
    description:
      "Gestiona los emisores de facturación de tu cuenta y vincula cada alojamiento al que corresponda.",
  },

  intro: {
    lead:
      "Un <strong>emisor de facturación</strong> es la entidad fiscal que factura a tus huéspedes. Puedes tener varios y elegir con cuál se factura cada alojamiento.",
    feats: {
      fiscalData: {
        label: "Datos fiscales propios",
        tip: "Razón social, NIF/CIF y dirección fiscal que aparecen en la factura.",
      },
      series: {
        label: "Serie independiente",
        tip: "Cada emisor lleva su propia serie y correlativo (y de rectificativas/abonos), sin mezclarse.",
      },
      verifactu: {
        label: "VeriFactu por emisor",
        tip: "Cada emisor cumple VeriFactu (firma y registro ante la AEAT) por separado, según su config fiscal.",
      },
      assignment: {
        label: "Asignación por alojamiento",
        tip: "Vincula cada alojamiento a un emisor. Los no asignados usan el emisor principal.",
      },
    },
    foot: "Al emitir, se usa el emisor del alojamiento de la reserva (al check-out o cuando lo pide el huésped al agente IA).",
  },

  issuers: {
    card: {
      title: "Emisores de facturación",
      subtitle: "Crea varios emisores y asigna a cada uno tus alojamientos.",
    },
    searchPlaceholder: "Buscar emisor por nombre o NIF…",
    newButton: "Nuevo emisor",
    noName: "Sin nombre",
    defaultBadge: "Principal",
    listingsCount: "{count} aloj.",
    empty: {
      noMatch: "Ningún emisor coincide con la búsqueda.",
      none: "Aún no tienes emisores. Crea el primero con “Nuevo emisor”.",
    },
  },

  assign: {
    card: {
      title: "Asignación de alojamientos",
      subtitle:
        "Elige qué emisor factura cada alojamiento. Un alojamiento solo puede tener un emisor; los no asignados usan el principal.",
    },
    searchPlaceholder: "Buscar alojamiento…",
    filter: {
      all: "Todos",
      unassigned: "Sin asignar",
    },
    issuerFallback: "Emisor #{id}",
    principalSuffix: " (principal)",
    unassignedOption: "— Sin asignar (principal)",
    empty: {
      noMatch: "Ningún alojamiento coincide con el filtro.",
      none: "Aún no tienes alojamientos.",
    },
    count: "{shown} de {total} alojamientos",
  },

  drawer: {
    editTitle: "Editar emisor",
    newTitle: "Nuevo emisor",
    subtitle: "Datos fiscales del emisor de facturación",
    closeAria: "Cerrar",
    footer: {
      isDefault: "Es el principal",
      setDefault: "Marcar principal",
      delete: "Eliminar",
      cancel: "Cancelar",
      saving: "Guardando...",
      save: "Guardar emisor",
    },
  },

  company: {
    title: "Datos de la empresa",
    subtitle: "Información fiscal de tu propiedad",
    country: {
      label: "País de facturación",
      hint: "Elige el país fiscal que aplicará a esta configuración.",
    },
    personType: {
      label: "Tipo de persona",
      company: "Persona jurídica (empresa)",
      individual: "Persona física (autónomo)",
    },
    legalName: {
      label: "Nombre / Razón social",
      placeholderCompany: "Ej: INNDEV S.L.",
      placeholderIndividual: "Ej: María García",
      hint: "Nombre fiscal de la empresa o persona",
    },
    co: {
      regime: {
        label: "Régimen DIAN",
        common: "Régimen Común",
        simplified: "Régimen Simplificado",
        hint: "Obligatorio para facturación electrónica DIAN",
      },
      municipality: {
        label: "Municipio DIAN",
        hint: "Código DIVIPOLA de tu municipio — obligatorio para la DIAN.",
        selected: "Seleccionado: {code}",
      },
      responsibilities: {
        label: "Responsabilidades fiscales",
        placeholder: "Ej: O-13, R-99-PN",
        hint: "Códigos separados por coma. Más común: <strong>O-13</strong>",
      },
    },
    mx: {
      regime: {
        label: "Régimen fiscal (SAT)",
        placeholder: "Ej: 601",
        hint: "Código de régimen fiscal SAT",
      },
      zip: {
        label: "CP fiscal (SAT)",
        placeholder: "Ej: 06600",
        hint: "Código postal fiscal SAT",
      },
    },
  },

  address: {
    title: "Dirección fiscal",
    subtitle: "La dirección que aparece en las facturas",
    street: {
      label: "Calle y número",
    },
    city: {
      label: "Ciudad",
      hint: "Ciudad y código postal",
    },
    email: {
      label: "Email de facturación",
      placeholder: "facturacion@tupropiedad.com",
    },
    phone: {
      label: "Teléfono",
    },
    ph: {
      ES: {
        street: "Ej: Calle Gran Vía 42, piso 3",
        city: "Madrid",
        zip: "28001",
        regionLabel: "Provincia",
        region: "Madrid",
        phone: "+34 123 456 789",
      },
      CO: {
        street: "Ej: Calle 13 No. 10-25",
        city: "Bogotá",
        zip: "110111",
        regionLabel: "Departamento",
        region: "Cundinamarca",
        phone: "+57 601 234 5678",
      },
      MX: {
        street: "Ej: Av. Insurgentes Sur 1234",
        city: "Ciudad de México",
        zip: "06600",
        regionLabel: "Estado",
        region: "CDMX",
        phone: "+52 55 1234 5678",
      },
    },
  },

  bank: {
    title: "Cuenta para cobrar",
    subtitle: "Dónde te ingresan si cobras por transferencia",
    iban: {
      label: "Número de cuenta (IBAN)",
      placeholder: "ES91 2100 0418 4502 0005 1332",
      hint: "Sale impreso en la factura solo cuando la forma de pago es transferencia.",
      error: "El número de cuenta (IBAN) no tiene un formato válido.",
    },
    bic: {
      label: "BIC / SWIFT",
      placeholder: "CAIXESBB",
      hint: "Opcional. Hace falta para cobros desde fuera de la zona SEPA.",
    },
    holder: {
      label: "Titular de la cuenta",
      placeholder: "Déjalo en blanco si coincide con el nombre fiscal",
      hint: "Solo si la cuenta está a un nombre distinto del de la empresa.",
    },
  },

  invoice: {
    title: "Configuración de factura",
    subtitle: "Formato, moneda y numeración de tus documentos",
    currency: {
      label: "Moneda por defecto",
      hint: "Moneda en la que se emitirán las facturas",
    },
    taxRate: {
      label: "% de IVA por defecto",
      hintCO: "IVA general Colombia: <strong>19%</strong>",
      hintMX: "IVA general México: <strong>16%</strong>",
      hintES: "IVA reducido hostelería España: <strong>10%</strong> — general: <strong>21%</strong>",
    },
    type: {
      label: "Tipo de factura",
      detailed: {
        label: "Detallado",
        desc: "Cada servicio en una línea independiente",
      },
      simplified: {
        label: "Simplificado",
        desc: "Ticket o factura con ítems agrupados",
      },
    },
    numbering: {
      label: "Numeración de documentos",
      example: "Ej: {value}",
      invoices: {
        title: "Facturas",
      },
      series: "Serie",
      optional: "(opcional)",
      optionalPlaceholder: "Opcional",
      digits: "Dígitos",
      nextNumber: "Siguiente correlativo",
      hintOptional: "La serie es opcional. Si la dejas vacía, la factura se emitirá solo con el número correlativo.",
      hintFormat: "Si indicas una serie, el sistema generará el código con formato SERIE-NÚMERO. Si no indicas serie, generará solo NÚMERO.",
      hintWarn: "Ajusta este valor antes de emitir la primera factura de la serie para mantener la secuencia fiscal alineada.",
      hintSandboxCO: "En sandbox usa <strong>SETP</strong> — en producción debe coincidir con tu resolución DIAN.",
    },
    creditNotes: {
      title: "Notas de crédito",
      badge: "Anulaciones / rectificaciones",
      hint: "Se usa para la próxima factura rectificativa o anulación que emitas.",
    },
    footer: {
      label: "Rodapié por defecto",
      hint: "Texto que aparecerá al pie de todas las facturas",
      phES: "Ej: Operación sujeta a IVA reducido hostelería según art. 91 Ley IVA",
      phCO: "Ej: Resolución DIAN No. 000123 del 01-01-2025. Rangos del 1 al 5000000",
      phMX: "Ej: CFDI emitido conforme a requisitos del artículo 29-A del CFF",
    },
  },

  advanced: {
    title: "Opciones avanzadas",
    subtitle: "Configuración adicional de envío",
    attachPdf: {
      label: "Adjuntar PDF",
      text: "Incluir el documento PDF junto al enlace de descarga",
    },
  },

  loading: {
    default: "Cargando...",
    config: "Cargando configuración...",
    profiles: "Cargando emisores...",
    saving: "Guardando configuración...",
  },

  countries: {
    ES: "España",
    CO: "Colombia",
    MX: "México",
  },

  currencies: {
    EUR: "Euro",
    COP: "Peso colombiano",
    USD: "Dólar",
    MXN: "Peso mexicano",
  },

  taxId: {
    labelFallback: "ID Fiscal",
    helpES: "NIF para personas físicas, CIF para empresas. Ej: B12345678",
    helpCO: "NIT con dígito de verificación. Ej: 9014514812 (10 dígitos)",
    helpMX: "RFC de 12 o 13 caracteres. Ej: XAXX010101000",
    helpFallback: "Identificador fiscal",
  },

  validation: {
    taxIdES: "Formato inválido. Ej: B12345678",
    taxIdCO: "El NIT debe tener 9 o 10 dígitos. Ej: 9014514812",
    taxIdMX: "Formato RFC inválido. Ej: XAXX010101000",
  },

  msg: {
    reassignError: "No se pudo reasignar el alojamiento.",
    defaultUpdated: "Emisor principal actualizado.",
    setDefaultError: "No se pudo marcar como principal.",
    deleteConfirm: "¿Eliminar este emisor de facturación?",
    deleted: "Emisor eliminado.",
    deleteError: "No se pudo eliminar el emisor.",
    requiredFields: "Completa todos los campos obligatorios antes de guardar.",
    fixTaxId: "Corrige el identificador fiscal antes de guardar.",
    coRegimeRequired: "El régimen DIAN es obligatorio para Colombia.",
    coMunicipalityRequired: "El municipio DIAN es obligatorio para Colombia.",
    savedOk: "Emisor guardado correctamente.",
    saveError: "Error al guardar la configuración.",
  },

  misc: {
    thisIssuer: "este emisor",
  },
};
